<template>
    <div class="overflow-hidden">
        <div
            :class="['timeline overflow-hidden', direction, isHorizontal ? 'block pl-1' : 'flex pt-1']"
            :style="!isHorizontal && { height: `${maxEventOffset + 150}px` }"
        >
            <div :class="['side side-before', isHorizontal ? 'flex' : 'hidden']">
                <div
                    v-for="num in config.totalSideStacks[TimelineSide.BEFORE]"
                    :key="`side-stack-${TimelineSide.BEFORE}-${num}`"
                    class="stack"
                >
                    <PortalTarget
                        :name="`timeline-${$id}-side-stack-${TimelineSide.BEFORE}-${num}`"
                        multiple
                    />
                </div>
            </div>

            <div
                :class="[
                    'progress-container relative flex p-1.5 bg-black/5 rounded-l-full z-0 flex-shrink-0',
                    isHorizontal ? 'items-center my-2' : 'overflow-hidden w-6 rounded-r-full',
                ]"
            >
                <template v-if="isHorizontal">
                    <img
                        v-for="(event, index) of config.events"
                        :key="`dot-${index}`"
                        src="/images/logos/logo-studydrive.svg"
                        :style="{ 'margin-left': `${event.totalOffset}px` }"
                        :class="[
                            'event-dot absolute w-6 transition-opacity duration-500 ease-in-out',
                            activeEventIndex < index ? 'invisible opacity-0' : 'opacity-1',
                            isHorizontal ? 'block' : 'hidden',
                        ]"
                    />
                </template>
                <div
                    :class="['progress rounded-full', isHorizontal ? 'w-auto h-3' : 'w-3']"
                    :style="progressStyles"
                ></div>
            </div>

            <div :class="['side side-after', isHorizontal ? 'flex' : 'hidden']">
                <div
                    v-for="num in config.totalSideStacks[TimelineSide.AFTER]"
                    :key="`side-stack-${TimelineSide.AFTER}-${num}`"
                    class="stack flex"
                >
                    <PortalTarget
                        :name="`timeline-${$id}-side-stack-${TimelineSide.AFTER}-${num}`"
                        multiple
                    />
                </div>
            </div>

            <div class="event-list relative">
                <portal
                    v-for="(event, index) of config.events"
                    :key="index"
                    :to="`timeline-${$id}-side-stack-${event.side}-${event.sideStack}`"
                    :disabled="!isHorizontal"
                    :order="index"
                >
                    <img
                        v-if="!isHorizontal"
                        src="/images/logos/logo-studydrive.svg"
                        :class="[
                            'event-dot absolute w-6 transition-opacity duration-500 ease-in-out left-0 -ml-6 -mt-1',
                            activeEventIndex < index ? 'invisible opacity-0' : 'opacity-1',
                        ]"
                    />
                    <div
                        :ref="'events'"
                        :class="[
                            'event transition-opacity duration-500 ease-in-out',
                            {
                                'opacity-25': activeEventIndex > index,
                                active: activeEventIndex === index,
                                'invisible opacity-0': activeEventIndex < index,
                            },
                        ]"
                        :style="getEventStyle(event)"
                    >
                        <div class="title text-body-3 font-medium text-black">{{ event.title }}</div>
                        <div :class="['text-body-3 text-slate-600', { 'mb-3': !isHorizontal }]">
                            {{ event.text }}
                        </div>
                    </div>
                </portal>
            </div>
        </div>
    </div>
</template>

<script>
import dayjs from 'dayjs';
import { findLastIndex } from 'lodash-es';
import { TimelineSide, TimelineDirection } from './Timeline.types';

const scale = 4;
const eventDotSize = 24;

export default {
    name: 'Timeline',
    props: {
        /**
         * the following attributes are available per event
         * @see TimelineEvent in ./Timeline.types.ts
         */
        events: {
            type: Array,
            required: true,
        },
        /**
         * The direction of the timeline.
         */
        direction: {
            type: String,
            required: true,
            validator: (dir) => Object.values(TimelineDirection).includes(dir),
        },
        /**
         * Spacing between the events
         */
        stackGap: {
            type: [Number, String],
            default: 20,
        },
        /**
         * The width of events.
         */
        eventWidth: {
            type: [Number, String],
            default: 400,
        },
        /** Marks the progress of the timeline. If max is defined, it will be relativized */
        current: {
            type: [Number, String],
            default: 0,
        },
        /** Marks the max progress that should land on the last event. Initally, it will be the total offset of the last element. */
        max: {
            type: [Number, String],
            default: null,
        },
    },
    emits: ['rendered'],
    data() {
        return {
            activeEventIndex: null,
            maxEventOffset: 0,
        };
    },
    computed: {
        /**
         * defines their global offset regarding the progress
         */
        offsettedEvents() {
            let totalOffset = 0;

            return this.events.map((e, i) => {
                const d = dayjs(e.date);
                const offset =
                    i > 0 && e.date && !e.offset
                        ? Math.max(18 * scale, d.diff(dayjs(this.events[i - 1].date), 'month') * scale)
                        : e.offset || 0;
                totalOffset += offset;

                return {
                    ...e,
                    title: e.title || d.format('MMMM YYYY'),
                    offset,
                    totalOffset,
                };
            });
        },
        config() {
            const sideStacks = {
                [TimelineSide.BEFORE]: {
                    total: 0,
                    offsetEndLast: 0,
                },
                [TimelineSide.AFTER]: {
                    total: 0,
                    offsetEndLast: 0,
                },
            };

            const events = this.offsettedEvents.map((e) => {
                const sideStack = sideStacks[e.side];
                const shouldBreakStack = e.breakStack ?? sideStack.offsetEndLast + this.stackGap < e.totalOffset;

                // incase this side wasn't referenced before, initialize its stack amount
                sideStack.total = shouldBreakStack ? sideStack.total + 1 : Math.max(1, sideStack.total);
                sideStack.offsetEndLast = e.totalOffset + this.eventWidth;

                return {
                    ...e,
                    sideStack: sideStack.total,
                };
            });

            return {
                events,
                totalSideStacks: {
                    [TimelineSide.BEFORE]: sideStacks[TimelineSide.BEFORE].total,
                    [TimelineSide.AFTER]: sideStacks[TimelineSide.AFTER].total,
                },
            };
        },
        progressStyles() {
            return {
                [this.isHorizontal ? 'width' : 'height']: `${this.progress}px`,
            };
        },
        progressScale() {
            return this.max ? this.maxEventOffset / parseInt(this.max, 10) : 1;
        },
        progress() {
            return this.current * this.progressScale;
        },
        isHorizontal() {
            return this.direction === TimelineDirection.HORIZONTAL;
        },
    },
    watch: {
        async isHorizontal() {
            await this.$nextTick(); // let vue render the screen change first
            this.updateTimeline();
            this.$emit('rendered', this);
        },
        async progress() {
            this.updateTimeline();
        },
        events() {
            this.updateTimeline();
        },
    },
    created() {
        this.TimelineSide = TimelineSide;
    },
    mounted() {
        this.updateTimeline();
        this.$emit('rendered', this);
    },
    methods: {
        getEventStyle(event) {
            return {
                [this.isHorizontal ? 'marginLeft' : 'minHeight']: `${
                    this.isHorizontal ? event.totalOffset : event.offset
                }px`,
            };
        },
        updateActiveEventIndex() {
            const dotMargin = eventDotSize / 2 - 2; // all events are translated by 2px and the progressbar is additionally moved a bit too
            // in vertical mode, events may expand based on their content and increase in height, so we need to lookup the actual positioning
            const events = this.isHorizontal ? this.offsettedEvents : (this.$refs.events ?? []);
            const offsetType = this.isHorizontal ? 'totalOffset' : 'offsetTop';

            // calculation incldues additional margin for event dot, so an event will become active as soon as the progress hits the dot boundaries
            this.activeEventIndex = findLastIndex(events, (e) => e[offsetType] - dotMargin <= this.progress);
        },
        updateMaxEventOffset() {
            const numEvents = this.events.length;
            this.maxEventOffset = this.isHorizontal
                ? this.offsettedEvents[numEvents - 1]?.totalOffset || 0
                : this.$refs.events?.[numEvents - 1]?.offsetTop || 0;
        },
        updateTimeline() {
            this.updateMaxEventOffset();
            this.updateActiveEventIndex();
            this.adjustScroll();
        },
        adjustScroll() {
            const scrollDirection = this.isHorizontal ? 'scrollLeft' : 'scrollTop';
            const total = this.$el[this.isHorizontal ? 'offsetWidth' : 'offsetHeight'];

            // centers the progress in the container
            this.$el[scrollDirection] = Math.max(0, this.progress - total / 2);
        },
    },
};
</script>

<style lang="scss" scoped>
.timeline.vertical .progress {
    background: linear-gradient(0deg, #0691fb -0.01%, rgba(6, 145, 251, 0) 96.27%);
}

.timeline.horizontal .progress {
    background: linear-gradient(270deg, #0691fb 29.44%, rgba(6, 145, 251, 0) 98.87%);
}

.stack {
    @apply flex flex-col justify-end w-0;
}

.progress-container .event-dot {
    transform: translateX(calc(-50% + 2px));
}

.side {
    @apply relative w-full overflow-hidden pl-1.5;

    &-before {
        .stack {
            @apply pb-12;

            .event {
                &:not(:first-child) {
                    @apply mt-6;
                }

                &::after {
                    @apply block absolute bg-black;
                    content: '';
                    width: 1px;
                    height: 100%;
                    transform: translateX(2px);
                }
            }
        }
    }

    &-after {
        .stack {
            @apply pt-12;
            flex-direction: column-reverse;

            .event {
                &:not(:last-child) {
                    @apply mb-6;
                }

                &::before {
                    @apply block absolute bg-black;
                    content: '';
                    width: 1px;
                    height: 100%;
                    transform: rotate(180deg) translateX(-3px);
                    transform-origin: 0% 0%;
                }
            }
        }
    }

    .event.active::before {
        @apply bg-blue-600;
    }
}

.side .event {
    width: 400px;
}

.event-list {
    .event {
        @apply ml-5 mt-1.5;

        &:not(:last-child) {
            @apply mb-3;
        }
    }
}

.event {
    &.active {
        .title {
            @apply text-blue-600;
        }

        &::before {
            @apply bg-blue-600;
        }
    }
}

.timeline.vertical .progress-container {
    @apply rounded-b-none;
    height: inherit;

    &::after {
        position: absolute;
        bottom: 0;
        left: 0;
        height: 150px;
        width: 24px;
        content: '';
        box-shadow: inset 0px -100px 50px -25px transparent;
    }
}
</style>
